import { useCallback, useEffect, useState } from "react";
import Card from "./components/ui/Card";
import BaseTemplate from "./templates/BaseTemplate";
import { getInterpretationTheme } from "./api";
import { NavLink, useParams } from "react-router";
import { ChevronLeft } from "lucide-react";
import BadgeBrainRegion from "./components/ui/BadgeBrainRegion";
import Badge from "./components/ui/Badge";
import { BrainRegionText } from "./DiagnosticsPatternDetails";
import { getUniqueBrainAreasFromChannels } from "./lib/utils";

const InterpretationThemeDetails = () => {
  const [interpretationTheme, setInterpretationTheme] = useState([]);
  const { interpretationThemeId } = useParams();

  const brainAreas = getUniqueBrainAreasFromChannels(
    interpretationTheme?.theme_involved_channels ?? []
  );

  const fetchInterpretationTheme = useCallback(async () => {
    try {
      const response = await getInterpretationTheme(interpretationThemeId);
      setInterpretationTheme(response.data || []);
    } catch (err) {
      console.error("Error fetching patients:", err);
      // setError(err.response?.data?.detail || "Failed to fetch patients");
      setInterpretationTheme([]);
    }
  }, [interpretationThemeId]);

  useEffect(() => {
    fetchInterpretationTheme();
  }, [fetchInterpretationTheme]);

  return (
    <BaseTemplate>
      <Card name={"Interpretation Theme"} className={""}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs text-gray-400">ID: {interpretationThemeId}</p>
          </div>
          <div className="flex">
            <NavLink
              to={"/configuration"}
              className={
                "flex flex-row items-center text-sm text-gray-500 hover:text-gray-400 focus-visible:text-gray-400 transition-[color,translate] hover:translate-x-2 focus-visible:translate-x-2"
              }
            >
              <ChevronLeft size={"16"} />
              <span>Back to Diagnostics Center</span>
            </NavLink>
          </div>
          <Card name={"Interpretation Theme Details"} variant="secondary">
            <div className="flex flex-col gap-4">
              <p>{interpretationTheme.interpretation_text}</p>
              <p>
                <Badge>{interpretationTheme.source_type}</Badge>
              </p>
              {interpretationTheme.cited_references && (
                <i className="text-gray-500 font-light">
                  {interpretationTheme.cited_references}
                </i>
              )}
            </div>
          </Card>
          <Card name={"Involved Brain Areas"} variant="secondary">
            <div className="flex flex-row gap-2">
              {brainAreas.map((brainArea) => (
                <BadgeBrainRegion key={brainArea} region={brainArea} />
              ))}
            </div>
          </Card>
          <Card name={"Involved Channels and Bands"} variant="secondary">
            <div>
              <p>
                Channels:{" "}
                {interpretationTheme.theme_involved_channels?.map((channel) => (
                  <BrainRegionText key={channel} channel={channel}>
                    {`${channel} `}
                  </BrainRegionText>
                ))}
              </p>
              <p>
                Bands:{" "}
                {interpretationTheme.theme_involved_bands?.map((band) => (
                  <span key={band}>{`${band} `}</span>
                ))}
              </p>
            </div>
          </Card>
          <Card name={"Thresholds"} variant="secondary">
            {Array.isArray(interpretationTheme.thresholds) && interpretationTheme.thresholds.length > 0 ? (
              <div className="flex flex-row gap-2 items-center">
                {interpretationTheme.thresholds?.map((threshold, idx) => (
                  <Badge key={idx} className="flex flex-row gap-2">
                    <BrainRegionText
                      channel={threshold.channel}
                      className={"text-sm"}
                    >
                      {threshold.channel}
                    </BrainRegionText>
                    <div className="bg-gray-300 min-h-full w-px"></div>
                    <span className="text-sm">{threshold.band}</span>
                    <div className="bg-gray-300 min-h-full w-px"></div>
                    <span className="font-semibold text-sm">
                      {threshold.threshold}
                    </span>
                    <span className="text-sm">{`${threshold.type === "amplitude" ? "µV" : threshold.type === "percentage" ? "%" : ""}`}</span>
                  </Badge>
                ))}
              </div>
            ) : (
              <span>No thresholds defined</span>
            )}
          </Card>
          <Card name={"Keywords"} variant="secondary">
            <div className="flex gap-2 flex-wrap">
              {interpretationTheme.keywords?.map((keyword) => (
                <Badge key={keyword}>{keyword}</Badge>
              ))}
            </div>
          </Card>
        </div>
      </Card>
    </BaseTemplate>
  );
};

export default InterpretationThemeDetails;
